<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('attendance.fingerspot_attendance')">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{
                  includeEmployees: filters.includeEmployees?.param,
                }"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                :label="$t('attendance.processed_fingerspot')"
                :options="includeOpts"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <div
        v-if="
          $can.update('fingerspot_attendance_list') ||
          $can.create('fingerspot_attendance_list')
        "
        class="orangehrm-header-container"
      >
        <oxd-button
          v-if="$can.update('fingerspot_attendance_list')"
          :label="$t('attendance.fingerspot_autofix_all')"
          icon-name="wrench"
          display-type="secondary"
          :disabled="isLoading"
          @click="onBulkAutofix"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          ref="cardTable"
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :selectable="$can.delete('fingerspot_attendance_list')"
          :clickable="true"
          :loading="isLoading"
          class="orangehrm-employee-list"
          row-decorator="oxd-table-decorator-card"
          @click="onClickEdit"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
    <confirmation-dialog
      ref="autofixConfirmDialog"
      :title="autofixDialogTitle"
      :subtitle="autofixDialogSubtitle"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('general.confirm')"
      icon="wrench"
      confirm-button-type="secondary"
    />
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';
import {computed, ref} from 'vue';
import {navigate} from '@ohrm/core/util/helper/navigation';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import moment from 'moment';
import usei18n from '@/core/util/composable/usei18n';

const defaultSortOrder = {};

export default {
  components: {
    'confirmation-dialog': ConfirmationDialog,
    'delete-confirmation': DeleteConfirmationDialog,
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {$t} = usei18n();
    const dataNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          employeeId: item.employee.employeeId,
          fullName: item.employee.fullName,
          date: moment(item.scanDate.date, 'YYYY-MM-DD HH:mm:ss.SSSSSS').format(
            'dddd, D MMMM YYYY hh:mm:ss A',
          ),
          message: item.message,
          status: item.status,
          isSelectable: true,
          ioMode:
            item.ioMode === '1'
              ? $t('attendance.punch_in')
              : $t('attendance.punch_out'),
        };
      });
    };
    const includeOpts = [
      {
        id: 1,
        param: '',
        label: 'All',
      },
      {
        id: 2,
        param: 'POSTED',
        label: 'POSTED',
      },
      {
        id: 3,
        param: 'DRAFT',
        label: 'DRAFT',
      },
      {
        id: 4,
        param: 'ERROR',
        label: 'ERROR',
      },
      {
        id: 5,
        param: 'ADJUSTED',
        label: 'ADJUSTED',
      },
    ];
    const filters = ref({
      status: includeOpts[0],
    });
    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });
    const serializedFilters = computed(() => {
      return {
        status: filters.value.status?.param,
        sortField: sortField.value,
        sortOrder: sortOrder.value,
        empNumber: filters.value.employee?.id,
      };
    });
    const bulkAutofixEmpNumber = computed(() => filters.value.employee?.id);

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/fingerspot-attendance',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: dataNormalizer,
    });

    onSort(execQuery);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      bulkAutofixEmpNumber,
      sortDefinition,
      includeOpts,
    };
  },
  data() {
    return {
      checkedItems: [],
      autofixDialogTitle: '',
      autofixDialogSubtitle: '',
      rules: {
        employee: [],
      },
    };
  },
  computed: {
    headers() {
      return [
        {
          name: 'fullName',
          title: this.$t('general.full_name'),
          sortField: 'employee.fullName',
          style: {flex: 1},
        },
        {
          name: 'date',
          title: this.$t('general.time'),
          sortField: 'scanDate',
          style: {flex: 1},
        },
        {
          name: 'ioMode',
          title: this.$t('attendance.attendance_io_mode'),
          style: {flex: 1},
        },
        {
          name: 'message',
          title: this.$t('general.message'),
          sortField: 'message',
          style: {flex: 1},
        },
        {
          name: 'status',
          title: this.$t('attendance.processed_fingerspot'),
          sortField: 'status',
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionsCellRenderer,
        },
      ];
    },
  },
  methods: {
    actionsCellRenderer(...[, , , row]) {
      const cellConfig = {
        edit: {
          onClick: this.onClickEdit,
          props: {
            name: 'pencil-fill',
          },
        },
      };
      if (this.$can.delete('fingerspot_attendance_list')) {
        cellConfig.delete = {
          onClick: this.onClickDelete,
          component: 'oxd-icon-button',
          props: {
            name: 'trash',
          },
        };
      }
      if (
        row.status === 'ERROR' &&
        this.$can.update('fingerspot_attendance_list')
      ) {
        cellConfig.autofix = {
          onClick: this.onClickAutofix,
          component: 'oxd-icon-button',
          props: {
            name: 'wrench',
            title: this.$t('attendance.fingerspot_autofix'),
          },
        };
      }
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onClickAutofix(item, $event) {
      $event.stopImmediatePropagation();
      this.autofixDialogTitle = this.$t(
        'attendance.fingerspot_autofix_confirm_title',
      );
      this.autofixDialogSubtitle = this.$t(
        'attendance.fingerspot_autofix_confirm_message',
      );
      this.$refs.autofixConfirmDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.runAutofixSingle(item.id);
        }
      });
    },
    runAutofixSingle(id) {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/admin/fingerspot-attendance/${id}/autofix`,
          data: {},
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .finally(() => {
          this.isLoading = false;
          this.resetDataTable();
        });
    },
    onBulkAutofix() {
      this.autofixDialogTitle = this.$t(
        'attendance.fingerspot_autofix_all_confirm_title',
      );
      this.autofixDialogSubtitle = this.$t(
        'attendance.fingerspot_autofix_all_confirm_message',
      );
      this.$refs.autofixConfirmDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.runAutofixBulk();
        }
      });
    },
    runAutofixBulk() {
      const body = {};
      const empId = this.bulkAutofixEmpNumber;
      if (empId !== undefined && empId !== null) {
        body.empNumber = empId;
      }
      this.isLoading = true;
      this.http
        .request({
          method: 'POST',
          url: '/api/v2/admin/fingerspot-attendance/bulk-autofix',
          data: body,
        })
        .then((res) => {
          const payload = res.data?.data ?? {};
          const fixed = payload.fixedCount ?? 0;
          const failedList = payload.failed ?? [];
          const failedCount = failedList.length;
          let msg = this.$t('attendance.fingerspot_autofix_all_result', {
            fixed,
            failed: failedCount,
          });
          if (payload.truncated) {
            msg += ' ' + this.$t('attendance.fingerspot_autofix_all_truncated');
          }
          if (payload.draftPairingSkipped) {
            msg +=
              ' ' + this.$t('attendance.fingerspot_autofix_all_draft_skipped');
          } else if (payload.draftPairing) {
            const d = payload.draftPairing;
            const dCreated = (d.createdIds ?? []).length;
            const dFailed = d.failed ?? [];
            msg +=
              ' ' +
              this.$t('attendance.fingerspot_autofix_all_draft_result', {
                created: dCreated,
                failed: dFailed.length,
              });
            if (d.truncated) {
              msg +=
                ' ' + this.$t('attendance.fingerspot_draft_pairing_truncated');
            }
            if (dFailed.length > 0) {
              msg +=
                ' ' +
                this.formatFingerspotBulkFailures(
                  dFailed,
                  'attendance.fingerspot_autofix_all_draft_failed_details',
                );
            }
          }
          if (failedCount > 0) {
            msg +=
              ' ' +
              this.formatFingerspotBulkFailures(
                failedList,
                'attendance.fingerspot_autofix_all_failed_details',
              );
          }
          const warn =
            failedCount > 0 ||
            payload.truncated ||
            (payload.draftPairing &&
              ((payload.draftPairing.failed ?? []).length > 0 ||
                payload.draftPairing.truncated)) ||
            payload.draftPairingSkipped;
          const toast = warn ? this.$toast.warn : this.$toast.success;
          toast.call(this.$toast, {
            title: this.$t('attendance.fingerspot_autofix_all'),
            message: msg,
          });
        })
        .finally(() => {
          this.isLoading = false;
          this.resetDataTable();
        });
    },
    formatFingerspotBulkFailures(rows, detailsUnitId, maxShown = 12) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return '';
      }
      const slice = rows.slice(0, maxShown);
      const details = slice
        .map((r) => `${r.id}: ${r.message ?? ''}`)
        .join(' | ');
      let out = this.$t(detailsUnitId, {details});
      if (rows.length > maxShown) {
        out +=
          ' ' +
          this.$t('attendance.fingerspot_autofix_all_failed_more', {
            n: rows.length - maxShown,
          });
      }
      return out;
    },
    onClickAdd() {
      //   navigate('/pim/addEmployee');
    },
    onClickEdit($event) {
      const id = $event.id ? $event.id : $event.item?.id;
      navigate('/attendance/editFingerspotAttendance/{id}', {id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item, $event) {
      $event.stopImmediatePropagation();
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>

<style src="./fingerspot-attendance.scss" lang="scss" scoped></style>
